import './App.css';
import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useData } from './hooks/useData';
import { useCalculator } from './hooks/useCalculator';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import { SectionCard } from './components/SectionCard/SectionCard';
import { Calculator } from './components/Calculator/Calculator';
import { Summary } from './components/Summary/Summary';
import { CurrencySelector } from './components/CurrencySelector/CurrencySelector';
import { LocaleSelector } from './components/LocaleSelector/LocaleSelector';
import { AgreementPopup } from './components/AgreementPopup/AgreementPopup';
import { AgreementButton } from './components/AgreementButton';
import { QuestionsList } from './components/QuestionsList';
import LoginPage from './components/LoginPage';
import AdminLoginPage from './components/AdminLoginPage';
import AdminPage from './components/AdminPage';
import { t } from './locales/translations';

function AppContent() {
  const { companyName, sections, options, defaultOptions, isLoading, error } = useData();
  const {
    selectedOptions,
    selectedOptionsWithQuantities,
    defaultOptions: calculatorDefaultOptions,
    totalPrices,
    projectDeadline,
    hourlyOptions,
    handleOptionToggle,
    updateOptionQuantity,
    getOptionQuantity,
    clearSelections,
    validateSelections
  } = useCalculator(sections, defaultOptions, options);
  const { selectedLocale } = useLocale();
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const { isAuthenticated, isAdmin, isLoading: authLoading, signOut } = useAuth();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('loading', selectedLocale)}</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Admin accounts have no client project — send them to the admin panel
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (isLoading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('loading', selectedLocale)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="error">
          <h2>{t('error', selectedLocale)}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // const validation = validateSelections();

  return (
    <div className="app">
      {/* Fixed Header with Controls */}
      <header className="app-fixed-header">
        <div className="container">
          <div className="fixed-header-content">
            <div className="header-branding">
              <a
                href="https://dianalapteva.com"
                target="_blank"
                rel="noopener noreferrer"
                className="header-logo-link"
              >
                <img src="/diana_logo_monochrome.png" alt="Diana Lapteva" className="header-logo-small" />
              </a>
              <div className="header-text-compact">
                <h1 className="app-title-compact">{t('appTitle', selectedLocale)}</h1>
                <p className="app-subtitle-compact">{companyName}</p>
              </div>
            </div>
            <div className="header-controls">
              <CurrencySelector />
              <LocaleSelector />
              <button
                onClick={signOut}
                className="logout-button"
                title={t('logout', selectedLocale)}
              >
                {t('logout', selectedLocale)}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          <div className="content-grid">
            <div className="sections-panel">
              {/* Buttons Row */}
              <div className="buttons_row">
                <a
                  href="https://dianalapteva.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-back-link"
                >
                  ← {t('toMainWebsite', selectedLocale)}
                </a>
                <AgreementButton onClick={() => setIsAgreementOpen(true)} />
              </div>

              {/* Questions Section - displays after header and before sections */}
              <QuestionsList locale={selectedLocale} />

              {/* <h2 className="panel-title">{t('technologyStack', selectedLocale)}</h2> */}
              <div className="sections-list">
                {sections.map(section => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    options={options}
                    selectedOptions={selectedOptions}
                    onOptionToggle={handleOptionToggle}
                    onOptionQuantityChange={updateOptionQuantity}
                    getOptionQuantity={getOptionQuantity}
                  />
                ))}
              </div>
            </div>

            <div className="sidebar-panel">
              <div className="sticky_wrapper">
                <Calculator
                  selectedOptions={selectedOptions}
                  selectedOptionsWithQuantities={selectedOptionsWithQuantities}
                  defaultOptions={calculatorDefaultOptions}
                  totalPrices={totalPrices}
                  projectDeadline={projectDeadline}
                  hourlyOptions={hourlyOptions}
                  onClearSelections={clearSelections}
                />
              </div>
            </div>
          </div>
        </div>

        <Summary
          companyName={companyName}
          sections={sections}
          selectedOptions={selectedOptions}
          selectedOptionsWithQuantities={selectedOptionsWithQuantities}
          defaultOptions={calculatorDefaultOptions}
          totalPrices={totalPrices}
          projectDeadline={projectDeadline}
          hourlyOptions={hourlyOptions}
          validateSelections={validateSelections}
        />
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>{t('footer', selectedLocale)}</p>
        </div>
      </footer>

      <AgreementPopup
        isOpen={isAgreementOpen}
        onClose={() => setIsAgreementOpen(false)}
        companyName={companyName}
      />
    </div>
  );
}

/**
 * Admin route guard. Unauthenticated sessions — and client sessions that are not
 * admins — get the admin login form; admins get the panel.
 */
function AdminRoute() {
  const { isAuthenticated, isAdmin, isLoading: authLoading } = useAuth();
  const { selectedLocale } = useLocale();

  if (authLoading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('loading', selectedLocale)}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <AdminLoginPage />;
  }

  return <AdminPage />;
}

function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <AuthProvider>
          <CurrencyProvider>
            <Routes>
              <Route path="/" element={<AppContent />} />
              <Route path="/admin" element={<AdminRoute />} />
              <Route path="/admin/login" element={<AdminRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CurrencyProvider>
        </AuthProvider>
      </LocaleProvider>
    </BrowserRouter>
  );
}

export default App
